import { useNavigate, useParams } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  cn,
  FilledButton,
  LANGUAGES,
  Loader,
  scrollBarClasses,
  showError,
  useQueryParams
} from 'shared';

import {
  EIntegrationSettingsType,
  EPreconversationType,
  tavusPersonaStore
} from 'entities/tavus/persona';
import { replicaStore } from 'entities/tavus/replica/model/replicaStore';
import { conversationStore } from 'entities/tavus/conversation';
import { TavusEmbedConference } from './TavusEmbedConference';
import { Replica } from 'entities/tavus/replica';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { creditStore } from 'entities/credit';
import { EStatusSubscription } from 'entities/credit/api';
import { TavusCallEmbedForm } from './TavusCallEmbedForm';
import { getPermissionValue, Permissions } from 'shared/lib/getPermissionValue';
import PoweredBy from 'widgets/avatar-edit-persona-layout/ui/PoweredBy';

export const TavusCallEmbed = () => {
  const { startConversation, currentConversation, conversationLoading } =
    conversationStore((state) => ({
      startConversation: state.startConversation,
      currentConversation: state.currentConversation,
      conversationLoading: state.conversationLoading
    }));

  const { getSubscriptions, currentSubscription } = creditStore((state) => ({
    getSubscriptions: state.getSubscriptions,
    currentSubscription: state.currentSubscription
  }));

  const { fetchOne, currentPersona, setCurrentPersona } = tavusPersonaStore();
  const { id } = useParams();
  const query = useQueryParams();

  const key = query.get('key') || undefined;
  const type = query.get('type') as EIntegrationSettingsType;
  const disableFeatures = query.get('disableFeatures') === 'true';

  const [startedOnce, setStartedOnce] = useState(false);
  const [endedManually, setEndedManually] = useState(false);
  const [startingCall, setStartingCall] = useState(false);

  useEffect(() => {
    if (key) {
      getSubscriptions(key);
    }
  }, [key]);

  const [language, setLanguage] = useState<string | undefined>(
    currentPersona?.default_language || 'english'
  );

  useEffect(() => {
    if (currentPersona) {
      setLanguage(currentPersona.default_language);
    }
  }, [currentPersona]);

  const { fetchOneReplica } = replicaStore((state) => ({
    fetchOneReplica: state.fetchOne
  }));
  const [replica, setReplica] = useState<Replica | undefined>();

  useEffect(() => {
    getPersonaData(id);
  }, []);

  const windowFields =
    type === 'embed' ? currentPersona?.embed : currentPersona?.widget;

  const getPersonaData = async (id: string | undefined) => {
    if (!id) return showError("Can't get agent. Please check embed url");
    const persona = await fetchOne(id, false).then((success) => {
      !success && showError('You need to refresh your link');
      if (success) {
        return success;
      }
    });
    if (persona) {
      setCurrentPersona(persona);
      fetchOneReplica(persona.default_replica_id, key).then((replica) => {
        setReplica(replica);
      });
    }
  };

  const validationSchema = Yup.object(
    currentPersona?.preconversation_config?.fields.reduce((acc, field) => {
      if (field.type === EPreconversationType.EMAIL) {
        acc[field.name] = Yup.string()
          .email('Invalid email address')
          .required();
      } else {
        acc[field.name] = Yup.string().required();
      }
      return acc;
    }, {} as Record<string, Yup.StringSchema>)
  );

  const initialValues = useMemo(() => {
    return currentPersona?.preconversation_config?.fields.reduce(
      (acc, field) => {
        acc[field.name] = '';
        return acc;
      },
      {} as Record<string, string>
    );
  }, [currentPersona?.preconversation_config]);

  const formik = useFormik({
    initialValues: initialValues!,
    validationSchema,
    onSubmit: async (values) => {
      await startCall(values);
    },
    enableReinitialize: true
  });

  const startCall = async (data?: { [key: string]: string }) => {
    try {
      setStartingCall(true);
      if (
        (currentSubscription?.status !== EStatusSubscription.ACTIVE &&
          currentSubscription?.status !== EStatusSubscription.TRIAL &&
          currentSubscription?.status !== EStatusSubscription.CANCELLED) ||
        !currentSubscription
      ) {
        showError(
          'Your subscription is not active. Please check your subscription status.'
        );
        return;
      }
      if (!currentSubscription?.plan?.feature?.agents) {
        showError('Your subscription does not include the agents feature.');
        return;
      }
      if (!currentSubscription.plan?.feature?.embed) {
        showError('Your subscription does not include the embed feature.');
        return;
      }
      if (Number(currentSubscription?.amountSecondsLeft || 0) <= 0) {
        showError('You do not have enough seconds left.');
        return;
      }

      const conversation = await startConversation({
        currentPersona,
        key,
        language,
        data,
        preconversationId: undefined
      });

      localStorage.setItem('conversation_id', conversation.conversation_id);

      const url = new URL(window.location.href);
      url.searchParams.set('conversation_id', conversation.conversation_id);
      window.history.replaceState({}, '', url.toString());

      const params = new URLSearchParams(window.location.search);
      const disabledListParam = params.get('disablePermissions');
      const disabledList = disabledListParam
        ? disabledListParam.split(',').map((p) => p.trim())
        : [];

      window.parent.postMessage(
        {
          type: 'conversation_started',
          conversation_id: conversation.conversation_id,
          disabledList
        },
        '*'
      );
    } finally {
      setStartingCall(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const disabledListParam = params.get('disablePermissions');
    const disabledList = disabledListParam
      ? disabledListParam.split(',').map((p) => p.trim())
      : [];

    window.parent.postMessage(
      {
        type: 'conversation_started',
        disabledList
      },
      '*'
    );
  }, []);

  useEffect(() => {
    const startScreenEnabled =
      getPermissionValue({
        permissionKey: Permissions.START_SCREEN
      }) === true;

    if (
      (disableFeatures || startScreenEnabled) &&
      !currentConversation &&
      currentPersona &&
      !conversationLoading &&
      currentSubscription &&
      !startedOnce
    ) {
      startCall();
      setStartedOnce(true);
    }
  }, [
    disableFeatures,
    currentConversation,
    currentPersona,
    conversationLoading,
    currentSubscription,
    startedOnce
  ]);

  const currentDomain = window.location.hostname;
  if (
    windowFields?.domains?.length &&
    !windowFields?.domains?.includes(currentDomain)
  )
    return <div>This domain is not in the allowed list.</div>;

  const isFormEnabled = Boolean(
    currentPersona?.preconversation_config?.fields.length &&
      windowFields?.is_form_enabled
  );

  if (
    disableFeatures ||
    getPermissionValue({
      permissionKey: Permissions.START_SCREEN
    }) === true
  ) {
    if (endedManually && startedOnce && !currentConversation) {
      return (
        <div
          className={cn(
            'ag-w-full ag-h-screen ag-flex ag-items-center ag-justify-center ag-text-center',
            windowFields?.dark_mode
              ? 'ag-bg-neutral-900 ag-text-white'
              : 'ag-bg-white ag-text-neutral-900'
          )}
          style={{ fontFamily: windowFields?.font }}
        >
          <div className="ag-text-center ag-flex ag-flex-col ag-gap-2 ag-justify-center ag-items-center">
            <h2 className="ag-text-2xl ag-font-semibold">
              🌟 {!startingCall ? 'Call ended' : 'Starting new call...'}
            </h2>
            {!startingCall && (
              <ul className="ag-flex ag-flex-col ag-justify-center ag-items-center ag-text-gray-400 ag-text-lg">
                <li>Thank you for the conversation.</li>
                <li>If {"you'd"} like, you can start a new one!</li>
              </ul>
            )}
            {startingCall ? (
              <Loader size={30} />
            ) : (
              <FilledButton
                onClick={() => startCall()}
                className="ag-px-4 ag-py-2 ag-rounded-lg"
              >
                {windowFields?.button_text || 'Start new call'}
              </FilledButton>
            )}
          </div>
        </div>
      );
    }
    return (
      <div
        className={cn(
          'ag-w-full ag-h-screen',
          windowFields?.dark_mode
            ? 'ag-bg-neutral-900 ag-text-white'
            : 'ag-bg-white ag-text-neutral-900'
        )}
        style={{ fontFamily: windowFields?.font }}
      >
        {conversationLoading || !currentConversation ? (
          <div className="ag-w-full ag-h-screen ag-flex ag-items-center ag-justify-center">
            <Loader size={50} />
          </div>
        ) : (
          <TavusEmbedConference onCallEnd={() => setEndedManually(true)} />
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        `ag-w-full`,
        windowFields?.dark_mode === true
          ? 'ag-bg-neutral-900 ag-text-white'
          : 'ag-bg-white ag-text-neutral-900'
      )}
      style={{ fontFamily: windowFields?.font }}
    >
      {!currentPersona ? (
        <div className="ag-w-full ag-h-screen ag-flex ag-items-center ag-justify-center">
          <Loader size={50} />
        </div>
      ) : (
        <div className="ag-h-screen ag-flex ag-flex-col ag-overflow-hidden ag-relative">
          <>
            {conversationLoading ? (
              <div
                className={cn(
                  'ag-w-full ag-flex ag-items-center ag-justify-center ag-h-screen',
                  windowFields?.dark_mode ? 'ag-bg-neutral-900' : 'ag-bg-white'
                )}
              >
                <Loader size={50} />
              </div>
            ) : !currentConversation ? (
              <div
                className={cn(
                  'ag-w-full ag-h-screen ag-overflow-hidden',
                  scrollBarClasses
                )}
              >
                {/* background video */}
                {replica?.thumbnail_video_url ? (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    src={replica.thumbnail_video_url}
                    className="ag-w-full ag-h-screen ag-object-cover ag-absolute"
                  />
                ) : (
                  <div className="ag-w-full ag-h-screen ag-rounded-lg ag-object-cover ag-object-top ag-bg-neutral-100" />
                )}
                {windowFields?.powered_by && (
                  <div className="ag-absolute ag-bottom-5 ag-right-0 ag-left-0 ag-z-50 ag-flex ag-flex-col ag-justify-center ag-items-center">
                    <PoweredBy isPoweredBy={windowFields?.powered_by} />
                  </div>
                )}
                <div className="ag-absolute ag-top-0 ag-left-0 ag-right-0 ag-bottom-0 ag-z-20 ag-flex ag-items-center ag-justify-center ag-text-center">
                  {endedManually ? (
                    <div
                      className="ag-absolute ag-inset-0 ag-backdrop-blur-2xl ag-rounded-2xl ag-bg-black/60 ag-flex ag-flex-col ag-items-center ag-justify-center ag-text-center"
                      style={{ fontFamily: `${windowFields?.font}` }}
                    >
                      <div className="ag-flex flex-col ag-justify-center ag-items-center ag-gap-y-5 ag-h-full">
                        <h2 className="ag-text-white ag-text-2xl ag-font-bold">
                          Video call ended
                        </h2>
                        <p className="ag-text-white ag-text-sm">
                          {windowFields?.video_call_ended_custom_message}
                        </p>
                        <div
                          className={`ag-flex ag-mx-auto ag-duration-200 ag-transition-all ${
                            type === 'embed'
                              ? 'ag-flex-col sm:ag-flex-row'
                              : 'ag-flex-col ag-w-[90%]'
                          } ag-gap-4`}
                        >
                          {windowFields?.cta_primary_button_link ? (
                            <a
                              href={windowFields?.cta_primary_button_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ag-bg-purple-600 hover:ag-bg-purple-700 ag-text-white ag-px-6 ag-py-2 ag-rounded-md ag-text-sm"
                            >
                              {windowFields?.cta_primary_button_text}
                            </a>
                          ) : (
                            <FilledButton
                              type="button"
                              onClick={() => {
                                startCall();
                              }}
                              className="ag-bg-purple-600 !ag-text-sm hover:ag-bg-purple-700 ag-text-white ag-px-6 ag-py-2"
                            >
                              {windowFields?.cta_primary_button_text}
                            </FilledButton>
                          )}
                          {windowFields?.cta_secondary_button_text &&
                            windowFields?.cta_secondary_button_link && (
                              <a
                                href={windowFields.cta_secondary_button_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ag-border ag-rounded-md !ag-text-sm ag-duration-200 ag-border-white ag-text-white hover:ag-bg-white hover:ag-text-black ag-px-6 ag-py-2"
                              >
                                {windowFields.cta_secondary_button_text}
                              </a>
                            )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="ag-backdrop-blur-2xl ag-bg-[#0000002f] ag-h-full ag-w-full ag-flex ag-flex-col ag-justify-center ag-items-center">
                      <div className="ag-flex ag-flex-col ag-gap-y-3 ag-justify-center ag-items-center ag-w-full ag-max-w-xl ag-px-6">
                        <div className="ag-font-semibold ag-text-2xl ag-text-white text-center">
                          {windowFields?.video_agent_name}
                        </div>

                        {windowFields?.description && (
                          <div className="ag-font-normal ag-text-xl ag-text-white text-center">
                            {windowFields?.description}
                          </div>
                        )}

                        <div className="ag-w-full ag-flex ag-flex-col ag-gap-y-4 ag-px-6">
                          {isFormEnabled && (
                            <TavusCallEmbedForm
                              preconversationConfig={
                                currentPersona?.preconversation_config
                              }
                              formik={formik}
                            />
                          )}

                          {startingCall ? (
                            <Loader size={30} />
                          ) : (
                            <FilledButton
                              type="button"
                              className="ag-w-full ag-py-2"
                              onClick={() => {
                                if (isFormEnabled) {
                                  formik.handleSubmit();
                                  return;
                                }
                                startCall();
                              }}
                              style={{
                                backgroundColor: windowFields?.main_color
                              }}
                            >
                              {windowFields?.button_text || 'Start new chat'}
                            </FilledButton>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  `ag-grow ag-shrink-0`,
                  windowFields?.dark_mode ? 'ag-bg-neutral-900' : 'ag-bg-white'
                )}
              >
                <TavusEmbedConference
                  onCallEnd={() => setEndedManually(true)}
                />
              </div>
            )}
          </>
        </div>
      )}
    </div>
  );
};
